import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';
import styles from './styles.scss';

import classnames from 'classnames';
import { useLocation, useParams } from 'react-router-dom';
import { deserializeListViewState } from '../../../utils/rx-store-serialization';
import { formatISOTimestamp } from '../../../utils/time';

import BackButton from '../../BackButton';
import {
  ActionBarFilters,
  ActionBarSearch,
  FilterOperatorInputPercent,
} from '../../ActionBar';

import {
  AppBar,
  AppBarSection,
  AppBarTitle,
  Button,
  Checkbox,
  Icons,
  InputBox,
  RadioButton,
  Toaster,
} from '@densityco/ui';
import ListView, { ListViewColumn } from '../../ListView';
import HorizontalForm from '../../HorizontalForm';
import colorVariables from '@densityco/ui/variables/colors.json';

import SingleCaptureViewer from '../../CaptureViewer/SingleCaptureViewer';
import ColoredPercent from '../../ColoredPercent';
import ListViewTwoRowHeader from '../../ListViewTwoRowHeader';
import Loading from '../../Loading';
import ErrorState from '../../ErrorState';
import CaptureTagViewer from '../../CaptureTagViewer';
import TriggerDataCaptureButton from '../ActionButtons/TriggerDataCapture';
import StopDataCaptureButton from '../ActionButtons/StopDataCapture';
import { AddNotePopup } from '../../AddUpdateNoteForm';

import {
  ConfigStatus,
  ConfigType,
  ConfigAlgorithmType,
  HighlightableCapture,
  SensorConfigActionTypes,
  WorkingConfig,
} from '../../../types/sensor-configuration';
import { ResourceStatus } from '../../../types/resource';
import { FilterOperator } from '../../../types/filters';

import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import ConfigStore from '../../../rx-stores/sensor-configuration';
import { useRequiredSessionToken } from '../../../utils/session-token';
import { ON_PREM } from '../../../utils/environment';

import { SenseiApi } from '../../../utils/api';
import FormLabel from '../../FormLabel';

const MDASH = <Fragment>&mdash;</Fragment>;

const BoundsSelector: React.FunctionComponent<{
  upper: string;
  lower: string;
  onChangeUpper: (e) => void;
  onChangeLower: (e) => void;

  linked?: boolean;
  onChangeLinked?: (value: boolean) => void;
  hideLinked: boolean;
}> = props => (
  <div className={styles.boundsBoxes}>
    <InputBox
      type="number"
      placeholder="Upper"
      width="100%"
      leftIcon={
        <Icons.ArrowUp color={colorVariables.gray700} width={16} height={16} />
      }
      value={props.upper}
      onChange={props.onChangeUpper}
      data-test-id="sensor-configuration-trigger-line-upper-bound"
    />
    <InputBox
      type="number"
      placeholder="Lower"
      width="100%"
      leftIcon={
        <Icons.ArrowDown
          color={colorVariables.gray700}
          width={16}
          height={16}
        />
      }
      value={props.lower}
      onChange={props.onChangeLower}
    />
    {!props.hideLinked ? (
      <div
        className={classnames(styles.boundsBoxesLink, {
          [styles.linked]: props.linked,
        })}
        onClick={() => props.onChangeLinked(!props.linked)}
      >
        <div className={styles.boundsBoxesLinkIcon}>
          <Icons.Link color="currentColor" />
        </div>
      </div>
    ) : null}
  </div>
);

const SensorConfiguration: React.FunctionComponent<{}> = () => {
  const configState = useRxStore(ConfigStore);
  const dispatch = useRxDispatch();
  const { organizationIdOrAll, sensorSerialNumber } = useParams();
  const location = useLocation();
  const jwtToken = useRequiredSessionToken();

  const [commitNotePopupOpen, setCommitNotePopupOpen] = useState(false);
  const [editorValue, setEditorValue] = useState('');
  const [reannotateCaptures, setReannotateCaptures] = useState(false);

  const [
    capturePlayerVideoRef,
    setCapturePlayerVideoRef,
  ] = useState<HTMLVideoElement | null>(null);

  const reportsCurrentlyInProgress =
    configState.reports.capturesBeingRun.length;
  const numberOfCapturesComplete = Object.keys(configState.reports.results)
    .length;

  useEffect(() => {
    const {
      filters,
      searchText,
      sort,
      extra,
    } = deserializeListViewState(location.search.replace(/^\?/, ''), [
      'capture_id',
      't',
    ]);
    dispatch({
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_PAGE,
      sensorSerialNumber,
      params: {
        capture_id: parseInt(extra['capture_id'], 10) || undefined,
        t: parseFloat(extra['t']) || undefined,
      },
    });

    return () => {
      dispatch({ type: SensorConfigActionTypes.SENSOR_CONFIG_LEAVE_PAGE });
    };
  }, []);

  switch (configState.config.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      return <Loading />;
    case ResourceStatus.ERROR:
      return <ErrorState error={configState.config.error} />;
    case ResourceStatus.COMPLETE:
      const visibleCaptures =
        configState.captures.resource.status === ResourceStatus.COMPLETE
          ? configState.captures.resource.data
          : [];

      const selectedCapture =
        configState.captures.resource.status === ResourceStatus.COMPLETE
          ? configState.captures.resource.data.find(
              i => i.id === configState.captures.selectedId,
            )
          : null;

      const noCapturesWereLoaded =
        configState.captures.resource.status === ResourceStatus.COMPLETE &&
        configState.captures.selectedId === null;

      // Parse the max pixel intensity into a number to use for form validation below.
      const parsedMaxPixelIntensity =
        configState.config.data.workingConfig.type !== ConfigType.RAW
          ? parseInt(
              configState.config.data.workingConfig.maxPixelIntensity,
              10,
            )
          : 0;

      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              {configState.config.data.status === ConfigStatus.NEW
                ? 'Create'
                : 'Update'}{' '}
              Configuration: {configState.config.data.sensor.doorway_name || configState.config.data.sensor.serial_number}
            </AppBarTitle>
            <AppBarSection>
              <HorizontalForm>
                <Button
                  variant="underline"
                  href={`#/${organizationIdOrAll}/sensors/${sensorSerialNumber}`}
                >
                  Cancel
                </Button>
                <TriggerDataCaptureButton sensors={[configState.config.data.sensor]} />
                <StopDataCaptureButton sensors={[configState.config.data.sensor]} />
                <AddNotePopup
                  title="Configuration Summary"
                  buttonLabel="Commit & Send to Unit"
                  placeholder="Enter a comment on why this configuration change was made"
                  submitDisabled={
                    configState.config.data.workingConfigValidationError !==
                    null
                  }
                  onSubmit={() => {
                    if (configState.config.status !== ResourceStatus.COMPLETE) {
                      return;
                    }

                    SenseiApi.putSensorConfiguration(
                      jwtToken,
                      configState.config.data.sensor.serial_number,
                      WorkingConfig.toSensorFormat(
                        configState.config.data.workingConfig,
                      ).config,
                      editorValue,
                      reannotateCaptures,
                    )
                      .then(() => {
                        Toaster.showToast({
                          text: 'Successfully sent configuration.',
                        });
                      })
                      .catch(() => {
                        Toaster.showToast({
                          text: 'Failed to configure unit.',
                          type: 'error',
                        });
                      });

                    window.location.href = `#/${organizationIdOrAll}/sensors/${sensorSerialNumber}`;
                  }}
                  controls={
                    <Checkbox
                      checked={reannotateCaptures}
                      onChange={e => setReannotateCaptures(e.target.checked)}
                      label={`Send all captures for reannotation ${
                        configState.captures.resource.status === ResourceStatus.COMPLETE
                          ? `(${configState.captures.resource.data.length} ${configState.captures.resource.data.length === 1 ? 'capture' : 'captures'})`
                          : ''
                      }`}
                      data-test-id="resend-captures-for-annotation"
                    />
                  }
                  submitLabel="Commit Changes"
                  value={editorValue}
                  onChangeValue={setEditorValue}
                  open={commitNotePopupOpen}
                  onOpen={() => setCommitNotePopupOpen(true)}
                  onClose={() => setCommitNotePopupOpen(false)}
                  buttonVariant="filled"
                />
              </HorizontalForm>
            </AppBarSection>
          </AppBar>

          <div
            className={styles.wrapper}
            data-test-id="sensor-configuration-wrapper"
          >
            {noCapturesWereLoaded ? (
              <div className={styles.empty} data-test-id="sensor-configuration-empty">
                <h3>No captures found!</h3>
                <p>
                  An initial set of captures are automatically triggered when a sensor is
                  provisioned, so check back later.
                  <br />
                  Alternatively, to create captures manually, click{' '}
                  <strong>Trigger Data Capture</strong> above.
                </p>
              </div>
            ) : (
              <Fragment>
                <div className={styles.main}>
                  <div
                    className={styles.captures}
                    data-test-id="sensor-configuration-capture-list"
                  >
                    <AppBar>
                      <AppBarSection>
                        <ActionBarFilters
                          filterDefinitions={[
                            {
                              id: 'total_accuracy',
                              label: 'Total Accuracy',
                              operators: [
                                {
                                  id: 'lt',
                                  label: 'Less Than',
                                  allowMultiple: false,
                                  isValueValid: s => !isNaN(parseFloat(s)),
                                  initialValue: '',
                                  component: FilterOperatorInputPercent,
                                } as FilterOperator<string>,
                                {
                                  id: 'gt',
                                  label: 'Greater Than',
                                  allowMultiple: false,
                                  isValueValid: s => !isNaN(parseFloat(s)),
                                  initialValue: '',
                                  component: FilterOperatorInputPercent,
                                } as FilterOperator<string>,
                              ],
                            },
                          ].filter(
                            fil => !(ON_PREM && fil.id === 'total_accuracy'),
                          )}
                          filters={configState.captures.filters}
                          onChangeFilters={filters =>
                            dispatch({
                              type:
                                SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_FILTERS,
                              filters,
                            })
                          }
                          position={{top: 44, left: 0}}
                        />
                      </AppBarSection>
                      <AppBarSection>
                        <HorizontalForm>
                          <ActionBarSearch
                            searchValue={configState.captures.searchText}
                            onChangeSearch={e =>
                              dispatch({
                                type:
                                  SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SEARCH,
                                search: e.target.value,
                              })
                            }
                            searchPlaceholder="Search Capture Id"
                            searchWidth={250}
                          />
                        </HorizontalForm>
                      </AppBarSection>
                    </AppBar>
                    <div className={styles.captureList}>
                      {[ResourceStatus.IDLE, ResourceStatus.LOADING].includes(
                        configState.captures.resource.status,
                      ) ? (
                        <Loading />
                      ) : null}
                      {configState.captures.resource.status ===
                      ResourceStatus.ERROR ? (
                        <ErrorState error={configState.captures.resource.error} />
                      ) : null}
                      {configState.captures.resource.status ===
                      ResourceStatus.COMPLETE ? (
                        <ListView
                          padOuterColumns
                          data={visibleCaptures}
                          headerHeight={48}
                          isRowHighlighted={(capture: HighlightableCapture) =>
                            capture.highlighted
                          }
                          onClickRow={(i: HighlightableCapture) =>
                            dispatch({
                              type:
                                SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SELECTED,
                              selectedId: i.id,
                            })
                          }
                        >
                          <ListViewColumn
                            id="RadioButton"
                            title=" "
                            width={64}
                            template={(i: HighlightableCapture) =>
                              configState.captures.resource.status ===
                              ResourceStatus.COMPLETE ? (
                                <RadioButton
                                  checked={i.id === configState.captures.selectedId}
                                  onChange={() => {}}
                                />
                              ) : null
                            }
                          />
                          <ListViewColumn
                            id="ID"
                            width={100}
                            template={(i: HighlightableCapture) => (
                              <span
                                data-test-id={`sensor-configuration-capture-${i.id}`}
                              >
                                {i.id}
                              </span>
                            )}
                          />
                          <ListViewColumn
                            id="Date"
                            width={140}
                            template={(i: HighlightableCapture) => (
                              <div>
                                {formatISOTimestamp(i.created_at, true)}
                              </div>
                            )}
                          />
                          {!ON_PREM ? (
                            <ListViewColumn
                              id="Tags"
                              template={i => (
                                <CaptureTagViewer
                                  tags={i.tags.filter(
                                    tag => !tag.name.includes('F8'),
                                  )}
                                  onNavigateToTag={tag => {
                                    function navigateToFrame() {
                                      if (!capturePlayerVideoRef.paused) {
                                        setTimeout(navigateToFrame, 250);
                                      } else {
                                        capturePlayerVideoRef.currentTime =
                                          tag.start_frame / 30;

                                        dispatch({
                                          type:
                                            SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_VIEWER_SEEK,
                                          seek: capturePlayerVideoRef.currentTime,
                                        });
                                      }
                                    }
                                    if (i.id !== configState.captures.selectedId) {
                                      dispatch({
                                        type:
                                          SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SELECTED,
                                        selectedId: i.id,
                                      });
                                      // FIXME: a bad idea, really here it would be better to somehow schedule this code
                                      // to be called when the capture is selected.
                                      setTimeout(navigateToFrame, 250);
                                    } else {
                                      navigateToFrame();
                                    }
                                  }}
                                />
                              )}
                            />
                          ) : null}
                          {!ON_PREM ? (
                            <ListViewColumn
                              id="Starting Accuracy"
                              title={
                                <ListViewTwoRowHeader
                                  header="Accuracy"
                                  subheader="Starting"
                                />
                              }
                              template={item => (
                                <ColoredPercent
                                  percent={item.latest_report ? item.latest_report.total_accuracy : null}
                                />
                              )}
                              width={100}
                            />
                          ) : null}
                          {!ON_PREM ? (
                            <ListViewColumn
                              id="Experimental Accuracy"
                              title={
                                <ListViewTwoRowHeader
                                  header="Accuracy"
                                  subheader="Experimental"
                                />
                              }
                              template={i => {
                                const report = configState.reports.results[i.id];
                                return (
                                  <ColoredPercent
                                    percent={report ? report.totalAccuracy : null}
                                  />
                                );
                              }}
                              width={100}
                            />
                          ) : null}
                        </ListView>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.graphicalConfiguration}>
                    <div className={styles.captureViewer}>
                      {[ResourceStatus.IDLE, ResourceStatus.LOADING].includes(
                        configState.selectedCapture.status,
                      ) ? (
                        <div className={styles.captureViewerLoading}>
                          <p>Loading Captures</p>
                        </div>
                      ) : null}
                      <SingleCaptureViewer
                        configState={configState}
                        dispatch={dispatch}
                        capturePlayerVideoRef={capturePlayerVideoRef}
                        setCapturePlayerVideoRef={setCapturePlayerVideoRef}
                      />
                    </div>

                    <div className={styles.configuration}>
                      <AppBar>
                        <AppBarSection />
                        {!ON_PREM ? (
                          <AppBarSection>
                            <div className={styles.configurationStartingAccuracy}>
                              {selectedCapture && selectedCapture.latest_report ? (
                                <Fragment>
                                  Starting Accuracy:&nbsp;
                                  <ColoredPercent
                                    percent={
                                      selectedCapture.latest_report.total_accuracy
                                    }
                                  />
                                </Fragment>
                              ) : null}
                            </div>
                            <Button
                              disabled={configState.config.data.workingConfig.clean}
                              onClick={() =>
                                dispatch({
                                  type:
                                    SensorConfigActionTypes.SENSOR_CONFIG_RESET_CONFIGURATION,
                                })
                              }
                              data-test-id="sensor-configuration-reset"
                            >
                              Reset
                            </Button>
                          </AppBarSection>
                        ) : null}
                      </AppBar>
                      <div className={styles.configurationInner}>
                        <FormLabel
                          label="Type"
                          htmlFor="trigger-line-type"
                          input={
                            <InputBox
                              id="trigger-line-type"
                              type="select"
                              value={configState.config.data.workingConfig.type}
                              onChange={choice =>
                                dispatch({
                                  type:
                                    SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TYPE,
                                  configType: choice.id,
                                })
                              }
                              width={300}
                              choices={
                                configState.config.data.in_mpi
                                  ? [
                                      {
                                        id: ConfigType.TRIGGER_LINE_BOTTOM,
                                        label: 'Trigger Line',
                                      },
                                    ]
                                  : [
                                      {
                                        id: ConfigType.TRIGGER_LINE_BOTTOM,
                                        label: 'Trigger Line',
                                      },
                                      {
                                        id: ConfigType.SINGULARITY_BOTTOM,
                                        label: 'Singularity Bottom',
                                      },
                                      {
                                        id: ConfigType.SINGULARITY_WALL_RIGHT,
                                        label: 'Singularity Wall Right',
                                      },
                                      {
                                        id: ConfigType.SINGULARITY_WALL_LEFT,
                                        label: 'Singularity Wall Left',
                                      },
                                      {
                                        id: ConfigType.SINGULARITY_POLYGONAL,
                                        label: 'Point-by-point polygon',
                                      },
                                      {
                                        id: ConfigType.RAW,
                                        label: 'Raw',
                                      },
                                    ]
                              }
                            />
                          }
                        />
                        {configState.config.data.workingConfig.type !==
                        ConfigType.RAW ? (
                          <Fragment>
                            <FormLabel
                              label="Pixel Intensity"
                              htmlFor="pixel-intensity"
                              input={
                                <HorizontalForm>
                                  <InputBox
                                    id="pixel-intensity"
                                    type="number"
                                    placeholder="eg, 220"
                                    width={150}
                                    max="255"
                                    min="0"
                                    value={
                                      configState.config.data.workingConfig
                                        .maxPixelIntensity
                                    }
                                    onChange={e =>
                                      dispatch({
                                        type:
                                          SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_MAX_PIXEL_INTENSITY,
                                        maxPixelIntensity: e.target.value,
                                      })
                                    }
                                    data-test-id="sensor-configuration-pixel-intensity"
                                  />
                                  {!isNaN(parsedMaxPixelIntensity) &&
                                  parsedMaxPixelIntensity === 255 ? (
                                    <span
                                      className={
                                        styles.configurationInnerPixelIntensityDisabled
                                      }
                                    >
                                      * A value of <strong>255</strong> disables
                                      pixel intensity
                                    </span>
                                  ) : null}
                                </HorizontalForm>
                              }
                            />
                            {!isNaN(parsedMaxPixelIntensity) &&
                            parsedMaxPixelIntensity < 200 ? (
                              <div
                                className={
                                  styles.configurationInnerPixelIntensityWarning
                                }
                              >
                                <h3>Warning - Pixel Intensity is very low</h3>
                                <p>
                                  Pixel intensity values under 200 are usually not
                                  recommended, as they can cause the head to clip.
                                </p>
                              </div>
                            ) : null}
                            {!isNaN(parsedMaxPixelIntensity) &&
                            parsedMaxPixelIntensity > 255 ? (
                              <div
                                className={
                                  styles.configurationInnerPixelIntensityWarning
                                }
                              >
                                <h3>Warning - Pixel Intensity is too high</h3>
                                <p>
                                  Pixel intensity values above 255 are not
                                  supported.
                                </p>
                              </div>
                            ) : null}
                          </Fragment>
                        ) : null}

                        {/* Fields for each type of trigger line */}
                        {configState.config.data.workingConfig.type ===
                        ConfigType.TRIGGER_LINE_BOTTOM ? (
                          <div className={styles.configurationTriggerLineWrapper}>
                            <FormLabel
                              label="Trigger Line"
                              input={
                                <div className={styles.configurationTriggerLine}>
                                  <InputBox
                                    id="x0"
                                    type="number"
                                    leftIcon="x0"
                                    width="100%"
                                    value={
                                      configState.config.data.workingConfig
                                        .triggerLineX0
                                    }
                                    onChange={e =>
                                      dispatch({
                                        type:
                                          SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X0,
                                        x0: e.target.value,
                                      })
                                    }
                                  />
                                  <InputBox
                                    id="y0"
                                    type="number"
                                    leftIcon="y0"
                                    width="100%"
                                    value={
                                      configState.config.data.workingConfig
                                        .triggerLineY0
                                    }
                                    onChange={e =>
                                      dispatch({
                                        type:
                                          SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y0,
                                        y0: e.target.value,
                                      })
                                    }
                                    data-test-id="sensor-configuration-trigger-line-y0"
                                  />
                                  <InputBox
                                    id="x1"
                                    type="number"
                                    leftIcon="x1"
                                    width="100%"
                                    value={
                                      configState.config.data.workingConfig
                                        .triggerLineX1
                                    }
                                    onChange={e =>
                                      dispatch({
                                        type:
                                          SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X1,
                                        x1: e.target.value,
                                      })
                                    }
                                  />
                                  <InputBox
                                    id="y1"
                                    type="number"
                                    leftIcon="y1"
                                    width="100%"
                                    value={
                                      configState.config.data.workingConfig
                                        .triggerLineY1
                                    }
                                    onChange={e =>
                                      dispatch({
                                        type:
                                          SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y1,
                                        y1: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                              }
                            />
                            <FormLabel
                              label="Bounds"
                              input={
                                <BoundsSelector
                                  hideLinked={false}
                                  linked={
                                    configState.config.data.workingConfig
                                      .boundsLinked
                                  }
                                  onChangeLinked={linked =>
                                    dispatch({
                                      type:
                                        SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_BOUNDS_LINKED,
                                      linked,
                                    })
                                  }
                                  upper={
                                    configState.config.data.workingConfig.upperBound
                                  }
                                  onChangeUpper={e =>
                                    dispatch({
                                      type:
                                        SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_UPPER_BOUND,
                                      upperBound: e.target.value,
                                    })
                                  }
                                  lower={
                                    configState.config.data.workingConfig.lowerBound
                                  }
                                  onChangeLower={e =>
                                    dispatch({
                                      type:
                                        SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_LOWER_BOUND,
                                      lowerBound: e.target.value,
                                    })
                                  }
                                />
                              }
                            />
                          </div>
                        ) : null}

                        {configState.config.data.workingConfig.type ==
                          ConfigType.SINGULARITY_BOTTOM ||
                        configState.config.data.workingConfig.type ==
                          ConfigType.SINGULARITY_WALL_RIGHT ||
                        configState.config.data.workingConfig.type ==
                          ConfigType.SINGULARITY_WALL_LEFT ||
                        configState.config.data.workingConfig.type ==
                          ConfigType.SINGULARITY_POLYGONAL ? (
                          <FormLabel
                            label="Minimum Path Length"
                            htmlFor="min-path-length"
                            input={
                              <InputBox
                                type="number"
                                id="min-path-length"
                                placeholder="eg, 20"
                                width={150}
                                value={
                                  configState.config.data.workingConfig
                                    .minimumPathLength
                                }
                                onChange={e =>
                                  dispatch({
                                    type:
                                      SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_MINIMUM_PATH_LENGTH,
                                    minimumPathLength: e.target.value,
                                  })
                                }
                              />
                            }
                          />
                        ) : null}

                        {configState.config.data.workingConfig.type !== ConfigType.RAW ? (
                          <FormLabel
                            label="Algorithm Type"
                            htmlFor="algorithm-type"
                            input={
                              <InputBox
                                id="algorithm-type"
                                type="select"
                                value={configState.config.data.workingConfig.algorithm}
                                onChange={choice =>
                                  dispatch({
                                    type:
                                      SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_ALGORITHM,
                                    algorithm: choice.id,
                                  })
                                }
                                width={200}
                                choices={
                                  [
                                    {
                                      id: ConfigAlgorithmType.REGULAR,
                                      label: 'Regular',
                                    },
                                    {
                                      id: ConfigAlgorithmType.HDR,
                                      label: 'HDR',
                                    },
                                  ]
                                }
                              />
                            }
                          />
                        ) : null}

                        {configState.config.data.workingConfig.type ===
                        ConfigType.RAW ? (
                          <FormLabel
                            label="Raw Config JSON"
                            htmlFor="raw-config"
                            input={
                              <RawConfigEditor
                                invalid={
                                  configState.config.data
                                    .workingConfigValidationError !== null
                                }
                                value={configState.config.data.workingConfig.data}
                                onChange={rawConfig =>
                                  dispatch({
                                    type:
                                      SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_RAW_CONFIG,
                                    rawConfig,
                                  })
                                }
                              />
                            }
                          />
                        ) : null}
                        {configState.config.data.workingConfigValidationError ? (
                          <div className={styles.configurationInnerValidationError}>
                            {configState.config.data.workingConfigValidationError.message}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
                {!ON_PREM ? (
                  <AppBar>
                    <AppBarTitle>Quick Accuracy Reports</AppBarTitle>
                    <AppBarSection>
                      {reportsCurrentlyInProgress &&
                      configState.captures.resource.status ===
                        ResourceStatus.COMPLETE ? (
                        <div className={styles.reportsLoadingWrapper}>
                          <div className={styles.progressBarWrapper}>
                            <div className={styles.progressBar}>
                              <div
                                className={styles.progressBarInner}
                                style={{
                                  width: `${(numberOfCapturesComplete /
                                    configState.captures.resource.data.length) *
                                    100}%`,
                                }}
                              />
                            </div>
                            {numberOfCapturesComplete} capture(s) complete /&nbsp; 1
                            total capture(s)
                          </div>
                          <Button
                            onClick={() => {
                              dispatch({
                                type:
                                  SensorConfigActionTypes.SENSOR_CONFIG_RESET_REPORT,
                              });
                            }}
                            variant="underline"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="filled"
                          onClick={() => {
                            if (
                              configState.captures.resource.status !==
                              ResourceStatus.COMPLETE
                            ) {
                              return;
                            }
                            if (
                              configState.config.status !== ResourceStatus.COMPLETE
                            ) {
                              return;
                            }

                            dispatch({
                              type:
                                SensorConfigActionTypes.SENSOR_CONFIG_RESET_REPORT,
                            });

                            const config = WorkingConfig.toSensorFormat(
                              configState.config.data.workingConfig,
                            ).config;
                            const selectedId = configState.captures.selectedId;

                            SenseiApi.captureQuickAccuracy(
                              jwtToken,
                              selectedId,
                              config,
                              true,
                            ).then(data => {
                              dispatch({
                                type:
                                  SensorConfigActionTypes.SENSOR_CONFIG_SET_REPORT_COMPLETE,
                                capture_id: selectedId,
                                accuracy: data.accuracy,
                              });
                            });

                            dispatch({
                              type:
                                SensorConfigActionTypes.SENSOR_CONFIG_ADD_REPORT_LOADING,
                              capture_id: selectedId,
                            });
                          }}
                        >
                          Test Configuration
                        </Button>
                      )}
                    </AppBarSection>
                  </AppBar>
                ) : null}
              </Fragment>
            )}
          </div>
        </Fragment>
      );
  }
};

// A component used to edit "raw" configs via typing in JSON manually.
//
// The biggest difference from the density ui input box "textarea" is it
// has a monospace font.
const RawConfigEditor: React.FunctionComponent<{
  value: string;
  invalid: boolean;
  onChange: (string) => void;
}> = ({ value, invalid, onChange }) => (
  <textarea
    className={classnames(styles.rawConfigEditor, {
      [styles.invalid]: invalid,
    })}
    placeholder={`eg, {"filter": {"max_pixel_intensity_override": 255}}`}
    value={value}
    onChange={e => onChange(e.target.value)}
    data-test-id="sensor-configuration-raw-config"
  >
    {value}
  </textarea>
);

export default SensorConfiguration;
